package com.essntl.features.itinerary.ui.edit_itinerary_service

import core.dateformat.utils.formatTimestampToHHMM
import com.essntl.core.utils.utils.compareTables
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.*
import com.essntl.core.waypoint.domain.WaypointClient
import com.essntl.features.address.domain.repository.AddressRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.file.ui.FileState
import com.essntl.features.file.ui.FileViewModel
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.itinerary.domain.use_case.UpdateItineraryServiceUseCase
import com.essntl.features.message.domain.model.MessageModel
import com.essntl.features.message.domain.repository.MessageRepository
import com.essntl.features.message.domain.useCase.SendItineraryMessageUseCase
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.request.domain.repository.RequestRepository
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.AddTaskUseCase
import com.essntl.features.task.domain.use_case.DeleteTaskUseCase
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import com.essntl.toastmanager.ToastManager
import com.mohamedrejeb.richeditor.model.RichTextState
import core.config.FeatureConfig
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

class EditItineraryServiceViewModel(
    private val taskRepository: TaskRepository,
    private val messageRepository: MessageRepository,
    private val supplierRepository: SupplierRepository,
    private val serviceRepository: ServiceRepository,
    private val categoryRepository: CategoryRepository,
    private val addressRepository: AddressRepository,
    private val profileRepository: ProfileRepository,
    private val clientRepository: ClientRepository,
    private val requestRepository: RequestRepository,
    private val itineraryRepository: ItineraryRepository,
    private val updateItineraryServiceUseCase: UpdateItineraryServiceUseCase,
    private val updateTaskUseCase: UpdateTaskUseCase,
    private val deleteTaskUseCase: DeleteTaskUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val waypointClient: WaypointClient,
    private val toastManager: ToastManager,
    private val validation: EditItineraryServiceValidation,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<EditItineraryServiceState>,
    sendItineraryMessageUseCase: SendItineraryMessageUseCase,
    addTaskUseCase: AddTaskUseCase,
    fileRepository: FileRepository,
) : ViewModel {

    private val fileViewModel = FileViewModel(
        fileRepository = fileRepository,
        scope = scope,
        loadingManager = loadingManager,
    )

    init {
        collectFileState()
        getCurrentProfile()

        // Update Itinerary Service Use Case
        collectLoading(
            flow = updateItineraryServiceUseCase.state.map { it.loadingSet },
            setState = { ids ->
                if (state.value.itineraryService.value.id in ids)
                    state.update { s ->
                        s.copy(updateItineraryService = State.loading(s.updateItineraryService.data))
                    }
            },
        )
        collectSuccessOperationEvent(
            flow = updateItineraryServiceUseCase.successEvent,
            update = { _, new ->
                new.copy(supplier = new.supplier ?: state.value.itineraryService.value.supplier)
            },
            onSuccess = {
                // Only set status and supplier from changes, so we don't lose not saved data entered by the user
                state.value.itineraryService.value = state.value.itineraryService.value.copy(
                    status = it.status,
                    supplier = it.supplier ?: state.value.itineraryService.value.supplier,
                    address = it.address,
                )

                updateValidationValues(state.value.itineraryService.value)
            },
            stateGetSet = arrayOf(
                StateGetSet(
                    getState = { state.value.getItineraryService },
                    setState = { s -> state.update { it.copy(getItineraryService = s) } },
                ),
                StateGetSet(
                    getState = { state.value.updateItineraryService },
                    setState = { s -> state.update { it.copy(updateItineraryService = s) } },
                ),
            ),
        )

        // Add Task Use Case
        collectSuccessEvent(
            flow = addTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            getState = { state.value.getAllItineraryServiceTask },
            setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
        )

        // Edit Task Use Case
        collectLoading(
            flow = updateTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(updateTaskLoading = it) } },
        )
        collectSuccessEvent(
            flow = updateTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            update = { old, new -> new.copy(itineraryService = old.itineraryService) },
            getState = { state.value.getAllItineraryServiceTask },
            setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
        )

        // Delete Task Use Case
        collectLoading(
            flow = deleteTaskUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(deleteTaskLoading = it) } },
        )
        collectSuccessEvent(
            flow = deleteTaskUseCase.successEvent,
            predicate = { first, second -> first.id == second.id },
            getState = { state.value.getAllItineraryServiceTask },
            setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
        )

        // Get Current User Profile Use Case
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )

        // Send Itinerary Service Message Use Case
        collectLoading(
            flow = sendItineraryMessageUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(sendMessageLoading = it) } },
        )
    }

    private var getSharedAttachmentsStateJob: Job? = null
    private fun collectFileState() {
        // collecting attachments
        getSharedAttachmentsStateJob?.cancel()
        getSharedAttachmentsStateJob = scope.launch {
            fileViewModel.state.collect { data ->
                if (data.getAllFile.data.orEmpty().isNotEmpty()) {
                    getAllProfile()
                }

                isItineraryServiceChanged(data)
            }
        }
    }

    fun onEvent(event: EditItineraryServiceEvent) {
        when (event) {
            is EditItineraryServiceEvent.GetItineraryService ->
                getItineraryService(event)

            is EditItineraryServiceEvent.UpdateItineraryService ->
                updateItineraryService(event)

            is EditItineraryServiceEvent.GetSuppliers ->
                getSuppliers(event)

            is EditItineraryServiceEvent.ClearItineraryService ->
                clearItineraryService()

            is EditItineraryServiceEvent.ClearSupplier ->
                clearSupplier()

            is EditItineraryServiceEvent.UpdateProposal ->
                updateProposal(event)

            is EditItineraryServiceEvent.UpdateItineraryServiceFields ->
                updateItineraryServiceFields(event)

            is EditItineraryServiceEvent.UpdateItineraryStatus ->
                updateItineraryServiceStatus(event)

            is EditItineraryServiceEvent.UpdateItineraryServiceSupplier ->
                updateItineraryServiceSupplier(event)

            is EditItineraryServiceEvent.EditTaskStatus ->
                editTaskStatus(event)

            is EditItineraryServiceEvent.DeleteTask ->
                deleteTask(event)

            is EditItineraryServiceEvent.GetClientById ->
                getClientById(event)

            is EditItineraryServiceEvent.GetRequestById ->
                getRequestById(event)

            is EditItineraryServiceEvent.AddAttachments -> addAttachments(event)

            is EditItineraryServiceEvent.DeleteAttachment -> deleteAttachment(event)

            is EditItineraryServiceEvent.GetAllCategories -> getAllCategoryList()

            is EditItineraryServiceEvent.GetPlaces -> getAllPlaces(event)

            is EditItineraryServiceEvent.SelectPlace -> selectPlace(event)

            is EditItineraryServiceEvent.SetPlace -> setPlace()

            is EditItineraryServiceEvent.ClearPlace -> clearPlace()
        }
    }

    /**
     * Updating
     */

    private fun isItineraryServiceChanged(filesState: FileState? = null) {
        // Compare files and set
        val currentFileState = filesState ?: state.value.fileState
        val oldFiles = currentFileState.getAllFile.data?.map { it.id }.orEmpty()
        val newFiles = state.value.itineraryService.value.files.orEmpty().toList()

        val oldItineraryService = state.value.getItineraryService.data ?: ItineraryServiceModel.empty
        val newItineraryService = state.value.itineraryService.value
        val changedFields = oldItineraryService.getChangedFields(newItineraryService)

        state.update {
            it.copy(
                fileState = currentFileState,
                isEditEnabled = !changedFields.isEmpty ||
                    compareTables(oldFiles, newFiles),
            )
        }
    }

    private fun updateItineraryServiceFields(event: EditItineraryServiceEvent.UpdateItineraryServiceFields) {
        state.value.itineraryService.value = event.itineraryService
        isItineraryServiceChanged()
    }

    /**
     * Attachments
     */
    private fun addAttachments(event: EditItineraryServiceEvent.AddAttachments) =
        fileViewModel.addFiles(event.files)


    private fun deleteAttachment(event: EditItineraryServiceEvent.DeleteAttachment) =
        fileViewModel.deleteFile(event.index)

    /**
     * Current Profile
     */
    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    /**
     * Profile
     */
    private fun getProfile(id: String) =
        getData(
            getData = { profileRepository.getById(id) },
            getState = { state.value.getProfile },
            setState = { newState ->
                state.update {
                    it.copy(getProfile = newState)
                }
            },
        )

    private fun getAllProfile() =
        getData(
            getData = { profileRepository.getAll() },
            getState = { state.value.getAllProfile },
            setState = { newState ->
                state.update {
                    it.copy(getAllProfile = newState)
                }
            },
        )

    /**
     * Itinerary Service Events
     */

    private fun getItineraryService(event: EditItineraryServiceEvent.GetItineraryService) {
        fileViewModel.setState(State())

        getData(
            getData = {
                itineraryRepository.getItineraryService(event.itineraryServiceId)
            },
            getState = { state.value.getItineraryService },
            setState = { s ->
                state.update {
                    it.copy(
                        getItineraryService = s,
                        isEditEnabled = false,
                    )
                }
            },
            onSuccess = {
                val itineraryService = it
                    .copy(
                        editedDescription = RichTextState()
                            .apply { setHtml(it.editedDescription.orEmpty()) }
                            .toHtml(),
                    )

                state.value.itineraryService.value = itineraryService

                getAllItineraryServiceTasks(itineraryService.id)

                itineraryService.updatedBy?.let(::getProfile)

                updateValidationValues(itineraryService)

                val attachments = itineraryService.files.orEmpty()
                if (attachments.isNotEmpty())
                    fileViewModel.getFiles(attachments.toList())
                else
                    fileViewModel.setState(State.success(emptyList()))

                getAllMessages(itineraryService.id)
            },
        )

        getData(
            getData = {
                itineraryRepository.get(event.itineraryId)
            },
            getState = { state.value.getItinerary },
            setState = { s -> state.update { it.copy(getItinerary = s) } },
        )
    }

    private fun getAllMessages(itineraryServiceId: String) =
        getData(
            getData = { messageRepository.getMessagesByItineraryServiceId(id = itineraryServiceId) },
            getState = { state.value.getAllMessages },
            setState = { newState -> state.value = state.value.copy(getAllMessages = newState) },
            onSuccess = {
                val whatsappMessages = it.mapNotNull { msg ->
                    when (msg.type) {
                        is MessageModel.Type.Whatsapp -> msg
                        else -> null
                    }
                }

                val emailMessages = it.mapNotNull { msg ->
                    when (msg.type) {
                        is MessageModel.Type.Email -> msg
                        else -> null
                    }
                }

                if (whatsappMessages.isNotEmpty() && FeatureConfig.hasWhatsapp)
                    whatsappMessages.map { msg ->
                        messageRepository.getWhapiMessageById(msg.platformId)
                            .onSuccess { whapiMessage ->
                                val messagesSet =
                                    (state.value.whapiMessagesList.data.orEmpty() + whapiMessage).toSet()
                                state.update { state ->
                                    state.copy(
                                        whapiMessagesList = State.success(messagesSet.toList()),
                                    )
                                }
                            }
                    }

                if (emailMessages.isNotEmpty())
                    scope.launch {
                        state.update { state ->
                            state.copy(
                                waypointLogsList = State.loading(),
                            )
                        }

                        emailMessages.map { msg ->
                            waypointClient
                                .waypointLogs(msg.platformId)
                                .onSuccess { w ->
                                    val waypointSet =
                                        (state.value.waypointLogsList.data.orEmpty() + w).toSet()

                                    state.update { state ->
                                        state.copy(
                                            waypointLogsList = state.waypointLogsList.copy(
                                                data = waypointSet.toList(),
                                            ),
                                        )
                                    }
                                }
                                .onFailure {
                                    state.update { state ->
                                        state.copy(
                                            waypointLogsList = state.waypointLogsList.copy(isFailure = true),
                                        )
                                    }

                                    toastManager.error(description = "Failed to get waypoint logs")

                                    return@launch
                                }
                        }
                    }.invokeOnCompletion {
                        state.update { state ->
                            state.copy(
                                waypointLogsList = state.waypointLogsList.copy(isLoading = false),
                            )
                        }
                    }
            },
        )

    private fun getSuppliers(event: EditItineraryServiceEvent.GetSuppliers) =
        getData(
            getData = { serviceRepository.getServiceSuppliers(event.service.id) },
            getState = { state.value.getAllSuppliers },
            setState = { newState -> state.update { it.copy(getAllSuppliers = newState) } },
        )

    private fun getClientById(event: EditItineraryServiceEvent.GetClientById) {
        getData(
            getData = {
                clientRepository.getById(event.clientId)
            },
            setState = {},
            onSuccess = { client ->
                state.update { it.copy(client = client) }
            },
        )
    }

    private fun getRequestById(event: EditItineraryServiceEvent.GetRequestById) {
        getData(
            getData = {
                requestRepository.getById(event.requestId)
            },
            setState = {},
            onSuccess = { request ->
                state.update { it.copy(request = request) }
            },
        )
    }

    private fun updateValidationValues(itineraryService: ItineraryServiceModel) {
        validation.editItineraryServiceForm.reset()
        validation.editItineraryServiceForm.dateRange.firstValue = itineraryService.startDate
        validation.editItineraryServiceForm.dateRange.secondValue = itineraryService.endDate.orEmpty()
        validation.editItineraryServiceForm.timeRange.firstValue =
            formatTimestampToHHMM(itineraryService.startTime.orEmpty())
        validation.editItineraryServiceForm.timeRange.secondValue =
            formatTimestampToHHMM(itineraryService.endTime.orEmpty())
    }

    private fun updateItineraryService(event: EditItineraryServiceEvent.UpdateItineraryService) {
        scope.launch {
            // If delete files fails, we don't want to continue
            if (!fileViewModel.deleteFiles(
                    onSuccess = { fileUrlList ->
                        state.value.itineraryService.value = state.value.itineraryService.value.copy(
                            files = state.value.itineraryService.value.files
                                ?.filter { fileId ->
                                    fileUrlList.none { it.id == fileId }
                                }
                                ?.toSet(),
                        )
                    },
                    onFailure = {},
                )
            ) return@launch

            // If insert files fails, we don't want to continue
            if (!fileViewModel.insertFiles(
                    folderName = "client",
                    bucketName = "files",
                    entityType = "proposal_service",
                    context = event.context,
                    onSuccess = { files ->
                        state.value.itineraryService.value = state.value.itineraryService.value.copy(
                            files = state.value.itineraryService.value.files.orEmpty() + files.map { it.id },
                        )
                    },
                    onFailure = {},
                )
            ) return@launch

            val getItinerary = state.value.getItineraryService.data ?: return@launch

            val newItinerary = getItinerary
                .getChangedFields(state.value.itineraryService.value)

            scope.launch {
                updateItineraryServiceUseCase.update(
                    itineraryService = newItinerary.copy(supplier = state.value.itineraryService.value.supplier),
                    onSuccess = { _ ->
                        state.update {
                            it.copy(
                                isEditEnabled = false,
                            )
                        }
                    },
                )
            }
        }
    }

    private fun clearItineraryService() {
        state.update {
            it.copy(
                getItineraryService = State(),
                updateItineraryService = State(),
                getAllItineraryServiceTask = State(),
            )
        }

        fileViewModel.setState(State())

        state.value.itineraryService.value = ItineraryServiceModel.empty
    }

    private fun clearSupplier() =
        state.update {
            it.copy(
                getSupplier = State(),
            )
        }

    private fun updateProposal(event: EditItineraryServiceEvent.UpdateProposal) {
        val itinerary = state.value.getItinerary.data ?: ItineraryModel.empty

        state.update {
            it.copy(
                getItinerary = it.getItinerary.copy(
                    data = itinerary.copy(
                        proposal = event.proposal,
                    ),
                ),
            )
        }
    }

    private fun updateItineraryServiceStatus(event: EditItineraryServiceEvent.UpdateItineraryStatus) =
        scope.launch {
            updateItineraryServiceUseCase.update(
                itineraryService = event.itineraryService
                    .getChangedFields(
                        ItineraryServiceModel.empty.copy(
                            id = event.itineraryService.id,
                            status = event.status,
                        ),
                    )
                    .copy(
                        supplier = event.itineraryService.supplier,
                    ),
            )
        }

    private fun updateItineraryServiceSupplier(event: EditItineraryServiceEvent.UpdateItineraryServiceSupplier) =
        scope.launch {
            updateItineraryServiceUseCase.update(
                itineraryService = event.itineraryService
                    .getChangedFields(
                        ItineraryServiceModel.empty.copy(
                            id = event.itineraryService.id,
                            supplier = event.supplier,
                        ),
                    ),
            )

            getSupplier(event.supplier.id)
        }

    private fun getSupplier(supplierId: String) =
        getData(
            getData = { supplierRepository.get(supplierId) },
            getState = { state.value.getSupplier },
            setState = { data -> state.update { it.copy(getSupplier = data) } },
        )

    /**
     * Task Events
     */

    private var getAllItineraryServiceTasksJob: Job? = null
    private fun getAllItineraryServiceTasks(itineraryServiceId: String) {
        if (itineraryServiceId.isEmpty()) return

        getAllItineraryServiceTasksJob?.cancel()
        getAllItineraryServiceTasksJob = getData(
            getData = { taskRepository.getAllByItineraryServiceIds(listOf(itineraryServiceId)) },
            getState = { state.value.getAllItineraryServiceTask },
            setState = { s -> state.update { it.copy(getAllItineraryServiceTask = s) } },
        )
    }

    private fun editTaskStatus(event: EditItineraryServiceEvent.EditTaskStatus) =
        scope.launch {
            updateTaskUseCase.update(
                task = TaskModel.empty.copy(
                    id = event.taskId,
                    status = event.status,
                ),
            )
        }

    private fun deleteTask(event: EditItineraryServiceEvent.DeleteTask) =
        scope.launch {
            deleteTaskUseCase.delete(
                task = event.task,
            )
        }

    private fun getAllCategoryList() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllPlaces(event: EditItineraryServiceEvent.GetPlaces) {
        if (event.searchText.isEmpty())
            state.update { it.copy(getAllPlaces = State()) }
        else
            getData(
                getData = { addressRepository.getPlaces(event.searchText) },
                getState = { state.value.getAllPlaces },
                setState = { newState -> state.update { it.copy(getAllPlaces = newState) } },
            )
    }

    private fun selectPlace(event: EditItineraryServiceEvent.SelectPlace) =
        state.update {
            it.copy(selectedPlace = event.place)
        }

    private fun setPlace() {
        val placeId = state.value.selectedPlace?.placeId
        if (placeId.isNullOrEmpty()) return

        getData(
            getData = { addressRepository.setPlace(placeId) },
            getState = { state.value.setPlace },
            setState = { newState -> state.update { it.copy(setPlace = newState) } },
        )
    }

    private fun clearPlace() =
        state.update {
            it.copy(
                getAllPlaces = State(),
                setPlace = State(),
                selectedPlace = null,
            )
        }
}
